import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { useStreak } from '../contexts/StreakContext';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import { getCurrentDate, buildCurrentWeekDataFromDates, getCurrentWeekCompletion, toDateFormat } from '../util';
import { useRecommendedGame } from '../hooks/useRecommendedGame';
import { getUserProfile } from '../api/users';
import StreakIcons from './StreakIcons';
import Button from './Button';
import FaIcon from './FaIcon';
import styles from '../styles/SerieLandingPage.module.css';
import buttonStyles from '../styles/Button.module.css';

// Italian day names - Sunday first (domenica on the left)
const DAYS_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const DAYS_FULL_IT = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// Italian month names
const MONTHS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

// Generate dates for a specific month in calendar format (with empty slots for alignment)
const generateMonthDates = (year, month) => {
  // First day of the month
  const firstDay = new Date(year, month, 1);
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);
  
  const calendarDays = [];
  
  // Add empty slots for days before the first of the month
  // getDay() returns 0 for Sunday, which is our first column
  const startDayOfWeek = firstDay.getDay();
  for (let i = 0; i < startDayOfWeek; i++) {
    calendarDays.push(null); // Empty slot
  }
  
  // Add all days of the month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    calendarDays.push(toDateFormat(date));
  }
  
  return {
    dates: calendarDays,
    monthName: MONTHS_IT[month],
    year: year,
  };
};

// Helper function to get all dates in the current streak
const getStreakDates = (streakStartDate, currentStreak) => {
  if (!streakStartDate || !currentStreak || currentStreak === 0) {
    return new Set();
  }
  
  const streakDates = new Set();
  const startDate = new Date(streakStartDate + 'T00:00:00');
  
  for (let i = 0; i < currentStreak; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    streakDates.add(toDateFormat(date));
  }
  
  return streakDates;
};

// Streak milestones
const STREAK_MILESTONES = [
  { days: 7, label: 'Una settimana' },
  { days: 30, label: 'Un mese' },
  { days: 90, label: 'Tre mesi' },
  { days: 180, label: 'Sei mesi' },
  { days: 365, label: 'Un anno' },
];

// How streaks work info
const HOW_IT_WORKS = [
  {
    icon: 'fa-calendar-check',
    title: 'Completa una partita',
    description: 'Gioca e completa almeno una partita di qualsiasi gioco ogni giorno.',
    gradient: 'linear-gradient(145deg, #ff6b35, #f7931e)'
  },
  {
    icon: 'fa-fire',
    title: 'Mantieni la fiamma accesa',
    description: 'La tua serie continua solo se giochi ogni giorno, senza saltare.',
    gradient: 'linear-gradient(145deg, #ef4444, #dc2626)'
  },
  {
    icon: 'fa-trophy',
    title: 'Raggiungi traguardi',
    description: 'Raggiungi serie di 7, 30, 90, 180 e 365 giorni per celebrare i tuoi progressi.',
    gradient: 'linear-gradient(145deg, #f59e0b, #d97706)'
  }
];

// Benefits of maintaining streak
const BENEFITS = [
  {
    emoji: '💰',
    title: 'Senso di progresso',
    description: 'Vedi il tuo impegno crescere giorno dopo giorno e punta a nuovi record personali.'
  },
  {
    emoji: '📊',
    title: 'Traccia i progressi',
    description: 'Vedi la tua serie crescere e batti il tuo record personale.'
  },
  {
    emoji: '🎯',
    title: 'Motivazione quotidiana',
    description: 'La serie ti dà una ragione in più per tornare a giocare ogni giorno.'
  }
];

// Tips for maintaining streak
const TIPS = [
  {
    icon: 'fa-bell',
    title: 'Iscriviti alla newsletter',
    description: 'Ricevi promemoria utili a non perdere la tua serie.',
    href: '/newsletter'
  },
  {
    icon: 'fa-mobile-screen',
    title: 'Installa l\'app',
    description: 'Aggiungi i Giochini alla home per un accesso rapido ed immediato.',
    href: '/install'
  },
  {
    icon: 'fa-clock',
    title: 'Scegli un orario fisso',
    description: 'Gioca sempre alla stessa ora e crea la tua abitudine.'
  }
];

const SerieLandingPage = () => {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  const { streakData, isLoading } = useStreak();

  // Navigate to sign-in/sign-up page
  const handleAuthClick = () => {
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: hasEverAuthenticated ? 'sign_in' : 'sign_up',
      source_surface: 'streak_landing',
      gate_reason: 'streak_landing',
      next_path: '/serie',
      is_returning_device: hasEverAuthenticated,
      track_gate_viewed: true,
      trigger: 'landing_cta',
    });
    navigate(authUrl);
  };
  
  const currentDate = getCurrentDate();
  const currentStreak = streakData?.currentStreak || 0;
  const longestStreak = streakData?.longestStreak || 0;

  // Calendar state
  const today = new Date();
  const [displayMonth, setDisplayMonth] = useState(today.getMonth());
  const [displayYear, setDisplayYear] = useState(today.getFullYear());
  
  // Track completed dates by month (cached)
  const [completedDatesByMonth, setCompletedDatesByMonth] = useState({});
  const loadedMonthsRef = useRef(new Set());
  const [isLoadingMonthDates, setIsLoadingMonthDates] = useState(false);
  
  // Check if we're viewing the current month
  const isCurrentMonth = displayMonth === today.getMonth() && displayYear === today.getFullYear();

  // Generate calendar data for the selected month
  const calendarData = useMemo(() => {
    const firstDay = new Date(displayYear, displayMonth, 1);
    const lastDay = new Date(displayYear, displayMonth + 1, 0);
    return {
      ...generateMonthDates(displayYear, displayMonth),
      startDate: toDateFormat(firstDay),
      endDate: toDateFormat(lastDay)
    };
  }, [displayYear, displayMonth]);

  // Get all dates in the current streak (fallback calculation)
  const calculatedStreakDates = useMemo(() => 
    getStreakDates(streakData?.streakStartDate, currentStreak),
    [streakData?.streakStartDate, currentStreak]
  );

  // Load completed dates for the displayed month from backend
  useEffect(() => {
    const monthKey = `${displayYear}-${displayMonth}`;
    
    const loadMonthDates = async () => {
      // Check if already loaded - if so, skip
      if (loadedMonthsRef.current.has(monthKey)) {
        setIsLoadingMonthDates(false);
        return;
      }
      
      if (!isSignedIn) {
        // For anonymous users, use calculated streak dates
        setIsLoadingMonthDates(false);
        setCompletedDatesByMonth(prev => ({
          ...prev,
          [monthKey]: calculatedStreakDates
        }));
        loadedMonthsRef.current.add(monthKey);
        return;
      }
      
      // Set loading state BEFORE async operations
      setIsLoadingMonthDates(true);
      
      // Use a small delay to ensure state update is processed
      await new Promise(resolve => setTimeout(resolve, 0));
      
      try {
        const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
        const response = await getUserProfile(token, {
          start_date: calendarData.startDate,
          end_date: calendarData.endDate,
        });
        const completedDatesInRange = response?.app_resources?.streak?.completed_dates_in_range;

        if (Array.isArray(completedDatesInRange)) {
          const fetchedDates = new Set(completedDatesInRange);
          setCompletedDatesByMonth(prev => ({
            ...prev,
            [monthKey]: fetchedDates
          }));
        } else {
          // Fallback to calculated dates if backend doesn't return range data
          setCompletedDatesByMonth(prev => ({
            ...prev,
            [monthKey]: calculatedStreakDates
          }));
        }
        loadedMonthsRef.current.add(monthKey);
      } catch (err) {
        console.error('Error loading month completed dates:', err);
        // Fallback to calculated dates on error
        setCompletedDatesByMonth(prev => ({
          ...prev,
          [monthKey]: calculatedStreakDates
        }));
        loadedMonthsRef.current.add(monthKey);
      } finally {
        setIsLoadingMonthDates(false);
      }
    };
    
    loadMonthDates();
  }, [displayYear, displayMonth, calendarData.startDate, calendarData.endDate, isSignedIn, getToken, calculatedStreakDates]);

  // Get completed dates for current month, fallback to calculated if not loaded
  const streakDates = useMemo(() => {
    const monthKey = `${displayYear}-${displayMonth}`;
    const monthDates = completedDatesByMonth[monthKey];
    
    if (monthDates && monthDates.size > 0) {
      return monthDates;
    }
    
    // Fallback to calculated streak dates (for anonymous users or before fetch completes)
    return calculatedStreakDates;
  }, [displayYear, displayMonth, completedDatesByMonth, calculatedStreakDates]);

  // Navigate to previous month
  const goToPreviousMonth = useCallback(() => {
    setIsLoadingMonthDates(false); // Reset loading state when changing months
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  }, [displayMonth, displayYear]);

  // Navigate to next month (only if not current month)
  const goToNextMonth = useCallback(() => {
    if (isCurrentMonth) return;
    
    setIsLoadingMonthDates(false); // Reset loading state when changing months
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  }, [displayMonth, displayYear, isCurrentMonth]);
  
  // Check if user has completed today
  const todayStr = getCurrentDate();
  const hasCompletedToday = Array.isArray(streakData?.completedDatesCurrentWeek)
    ? streakData.completedDatesCurrentWeek.includes(todayStr)
    : false;
  
  // Get week completion data
  const weekData = buildCurrentWeekDataFromDates(streakData?.completedDatesCurrentWeek) || getCurrentWeekCompletion();
  
  // Get recommended game to play
  const { recommendation, isLoading: isLoadingRecommendation } = useRecommendedGame(currentDate, null, {
    subscribeOnly: true,
    allowBackgroundRevalidateOnSubscribe: true,
    priority: 'low',
  });

  // Calculate next milestone
  const getNextMilestone = () => {
    for (const milestone of STREAK_MILESTONES) {
      if (currentStreak < milestone.days) {
        return milestone;
      }
    }
    return null; // All milestones achieved
  };
  
  const nextMilestone = getNextMilestone();

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Floating flames background */}
        <div className={styles.floatingFlames}>
          {[...Array(10)].map((_, i) => (
            <div key={i} className={styles.floatingFlame}>
              🔥
            </div>
          ))}
        </div>

        {/* Sparkles */}
        <div className={styles.sparkles}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className={styles.sparkle} />
          ))}
        </div>

        <div className={styles.heroContent}>
          {/* Animated fire icon */}
          <div className={styles.iconShowcase}>
            <div className={styles.flameGlow} />
            <div className={styles.mainFlame}>
              <span className={styles.fireEmoji}>🔥</span>
            </div>
          </div>

          <h1 className={styles.heroTitle}>La tua Serie</h1>
          
          <p className={styles.heroSubtitle}>
            Gioca ogni giorno per mantenere accesa la tua fiamma 
            e costruire una serie sempre più lunga.
          </p>

          {isLoading ? (
            <div className={styles.loadingCard}>
              <span>Caricamento...</span>
            </div>
          ) : (
            <div className={styles.streakCard}>
              <div className={styles.streakDisplay}>
                <span className={styles.streakNumber}>{currentStreak}</span>
                <span className={styles.streakFlame}>🔥</span>
              </div>
              
              <div className={styles.streakLabel}>
                {currentStreak === 1 ? 'giorno consecutivo' : 'giorni consecutivi'}
              </div>
              
              <div className={styles.weekPreview}>
                <StreakIcons 
                  weekData={weekData}
                  showLabels={true}
                  theme="dark"
                />
              </div>
              
              {!hasCompletedToday && recommendation ? (
                <Link to={recommendation.path} className={styles.playButton}>
                  <FaIcon className="fas fa-play"  />
                  <span>Gioca ora per estendere</span>
                </Link>
              ) : (
                <div className={styles.completedBadge}>
                  <FaIcon className="fas fa-check"  />
                  <span>Serie estesa oggi!</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.scrollIndicator}>
          <span>Scopri di più</span>
          <FaIcon className={`fas fa-chevron-down ${styles.scrollArrow}`}  />
        </div>
      </section>

      {/* What is a Streak Section */}
      <section className={styles.whatSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Cos'è la serie?</h2>
          <p className={styles.sectionDescription}>
            La serie (o "streak") è il numero di giorni consecutivi in cui hai completato 
            almeno una partita. Ogni giorno che giochi, la tua serie cresce. 
            Ma attenzione: se salti anche solo un giorno, la serie riparte da zero!
          </p>
          
          <div className={styles.keyPoints}>
            {HOW_IT_WORKS.map((item, index) => (
              <div key={index} className={styles.keyPoint}>
                <div 
                  className={styles.keyPointIcon}
                  style={{ background: item.gradient }}
                >
                  <FaIcon className={`fas ${item.icon}`} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section (for logged-in users) */}
      {!isLoading && (
        <section className={styles.statsSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionEmoji}>📊</span>
              <h2 className={styles.sectionTitle}>Le tue statistiche</h2>
            </div>

            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>🔥</div>
                <div className={styles.statValue}>{currentStreak}</div>
                <div className={styles.statLabel}>Serie attuale</div>
              </div>
              
              <div className={styles.statCard}>
                <div className={styles.statIcon}>🏆</div>
                <div className={styles.statValue}>{longestStreak}</div>
                <div className={styles.statLabel}>Record personale</div>
              </div>
              
              <div className={styles.statCard}>
                <div className={styles.statIcon}>🎯</div>
                <div className={styles.statValue}>
                  {nextMilestone ? nextMilestone.days - currentStreak : '✓'}
                </div>
                <div className={styles.statLabel}>
                  {nextMilestone 
                    ? `Giorni al prossimo traguardo` 
                    : 'Tutti i traguardi raggiunti!'
                  }
                </div>
              </div>
            </div>

            {!isSignedIn && (
              <div className={styles.signInPrompt}>
                <p>
                  <FaIcon className="fas fa-info-circle"  />
                  Le statistiche mostrate sono basate sui dati locali. 
                  Accedi per sincronizzare i tuoi progressi su tutti i dispositivi.
                </p>
                <Button className={styles.signInButton} onClick={handleAuthClick}>
                  {hasEverAuthenticated ? 'Accedi' : 'Crea un account'}
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Streak Calendar Section */}
      {!isLoading && currentStreak > 0 && (
        <section className={styles.calendarSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionEmoji}>📅</span>
              <h2 className={styles.sectionTitle}>La tua serie</h2>
              <p className={styles.sectionSubtitle}>
                Vedi i giorni che compongono la tua serie
              </p>
            </div>

            {/* Month Navigation */}
            <div className={styles.calendarMonthNav}>
              <button 
                className={styles.calendarMonthNavButton}
                onClick={goToPreviousMonth}
                aria-label="Mese precedente"
              >
                <FaIcon className="fas fa-chevron-left"  />
              </button>
              <span className={styles.calendarMonthNavLabel}>
                {calendarData.monthName} {calendarData.year}
              </span>
              <button 
                className={`${styles.calendarMonthNavButton} ${isCurrentMonth ? styles.calendarMonthNavButtonDisabled : ''}`}
                onClick={goToNextMonth}
                disabled={isCurrentMonth}
                aria-label="Mese successivo"
              >
                <FaIcon className="fas fa-chevron-right"  />
              </button>
            </div>

            {/* Calendar Container */}
            <div className={styles.calendarContainer}>
              {/* Day headers */}
              <div className={styles.calendarDayHeaders}>
                {DAYS_IT.map((day, index) => (
                  <div key={day} className={styles.calendarDayHeader}>
                    <span className={styles.calendarDayShort}>{day}</span>
                    <span className={styles.calendarDayFull}>{DAYS_FULL_IT[index]}</span>
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className={styles.calendarGrid}>
                {calendarData.dates.map((date, index) => {
                  if (!date) {
                    // Empty slot
                    return <div key={`empty-${index}`} className={styles.calendarEmptySlot} />;
                  }

                  const dateObj = new Date(date + 'T00:00:00');
                  const dayNum = dateObj.getDate();
                  const isToday = date === currentDate;
                  const isFuture = date > currentDate;
                  const isInStreak = streakDates.has(date);
                  // Show loading for all non-future dates when loading month data (for signed-in users)
                  const showLoading = isLoadingMonthDates && !isFuture && isSignedIn;
                  
                  // Future dates are disabled
                  if (isFuture) {
                    return (
                      <div
                        key={date}
                        className={`${styles.calendarCard} ${styles.calendarCardFuture}`}
                      >
                        <span className={styles.calendarDate}>{dayNum}</span>
                      </div>
                    );
                  }

                  // Regular date with fire icon if in streak
                  return (
                    <div
                      key={date}
                      className={`${styles.calendarCard} ${isToday ? styles.calendarCardToday : ''} ${isInStreak ? styles.calendarCardStreak : ''} ${showLoading ? styles.calendarCardLoading : ''}`}
                    >
                      <span className={styles.calendarDate}>{dayNum}</span>
                      {isInStreak && (
                        <span className={styles.streakIcon}>🔥</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Milestones Section */}
      <section className={styles.milestonesSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🎁</span>
            <h2 className={styles.sectionTitle}>Traguardi della serie</h2>
            <p className={styles.sectionSubtitle}>
              Raggiungi questi traguardi per tenere viva la tua serie.
            </p>
          </div>

          <div className={styles.milestonesGrid}>
            {STREAK_MILESTONES.map((milestone, index) => {
              const isAchieved = currentStreak >= milestone.days;
              const isCurrent = nextMilestone && nextMilestone.days === milestone.days;
              
              return (
                <div 
                  key={index} 
                  className={`${styles.milestoneCard} ${isAchieved ? styles.achieved : ''} ${isCurrent ? styles.current : ''}`}
                >
                  <div className={styles.milestoneHeader}>
                    <span className={styles.milestoneDays}>{milestone.days}</span>
                    <span className={styles.milestoneDaysLabel}>giorni</span>
                  </div>
                  <div className={styles.milestoneLabel}>{milestone.label}</div>
                  {isAchieved && (
                    <div className={styles.achievedBadge}>
                      <FaIcon className="fas fa-check"  />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>✨</span>
            <h2 className={styles.sectionTitle}>Perché mantenere la serie?</h2>
          </div>

          <div className={styles.benefitsGrid}>
            {BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitCard}>
                <span className={styles.benefitEmoji}>{benefit.emoji}</span>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p className={styles.benefitDescription}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className={styles.tipsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>💡</span>
            <h2 className={styles.sectionTitle}>Consigli per non perdere la serie</h2>
          </div>

          <div className={styles.tipsGrid}>
            {TIPS.map((tip, index) => (
              <div key={index} className={styles.tipCard}>
                <div className={styles.tipIconWrapper}>
                  <FaIcon className={`fas ${tip.icon}`} />
                </div>
                <div className={styles.tipContent}>
                  <h3 className={styles.tipTitle}>{tip.title}</h3>
                  <p className={styles.tipDescription}>{tip.description}</p>
                  {tip.href && (
                    <Link to={tip.href} className={styles.tipLink}>
                      Scopri di più →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Domande frequenti</h2>
          
          <div className={styles.faqGrid}>
            <div className={styles.faqItem}>
              <h3>Quale gioco devo completare?</h3>
              <p>
                Puoi completare qualsiasi partita di qualsiasi gioco disponibile. 
                Non importa se vinci o perdi: l'importante è completare la partita.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Cosa succede se salto un giorno?</h3>
              <p>
                Se salti anche solo un giorno, la tua serie viene azzerata 
                e riparte da 1 il giorno successivo. Il record personale resta però salvato.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>A che ora si resetta il giorno?</h3>
              <p>
                Il giorno si resetta a mezzanotte nel tuo fuso orario locale. 
                Hai tempo fino alle 23:59 per completare una partita.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Posso recuperare una serie persa?</h3>
              <p>
                Purtroppo no: una volta persa, la serie non può essere recuperata. 
                Ma puoi sempre ricominciare e battere il tuo record!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className={styles.bottomCta}>
        <div className={styles.bottomCtaContent}>
          <span className={styles.bottomCtaIcon}>🔥</span>
          <h2 className={styles.bottomCtaTitle}>
            {hasCompletedToday 
              ? 'Continua così!' 
              : 'Mantieni la fiamma accesa!'
            }
          </h2>
          <p className={styles.bottomCtaText}>
            {hasCompletedToday
              ? 'Hai già completato la partita di oggi. Torna domani per continuare la serie!'
              : 'Gioca adesso per estendere la tua serie. Ogni giorno conta!'
            }
          </p>
          
          <Link to={hasCompletedToday ? '/' : (recommendation?.path || '/')} className={styles.bottomCtaButton}>
            <FaIcon className={`fas ${hasCompletedToday ? 'fa-home' : 'fa-play'}`}  />
            <span>{hasCompletedToday ? 'Torna alla home' : 'Gioca ora'}</span>
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default SerieLandingPage;
